import React, { useState, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { 
  FaShieldAlt, FaExclamationTriangle, FaSearch, 
  FaCheckCircle, FaSkullCrossbones, FaUserSecret, 
  FaFileContract, FaHardHat, FaHouseDamage, FaMoneyBillWave 
} from 'react-icons/fa';

import { useNavigate } from 'react-router-dom';

import './ScamDetector.css';

// --- FULL DATA: TOP 10 SCAMS ---
const scamDatabase = [
  {
    id: 1,
    title: "The Phantom Tradie",
    scenario: "A builder asks for a 50% cash deposit upfront for 'materials' and then never returns.",
    dangerLevel: "CRITICAL",
    solution: "Build Start Verification",
    education: "Never pay large deposits upfront. Build Start Pros are vetted for financial stability and identity.",
    icon: <FaSkullCrossbones />
  },
  {
    id: 2,
    title: "The License Borrower",
    scenario: "They show you a license card, but it actually belongs to their 'boss' or a friend.",
    dangerLevel: "HIGH",
    solution: "Photo ID Match",
    education: "We manually cross-check the photo ID against the Government License Database to ensure the person is who they say they are.",
    icon: <FaUserSecret />
  },
  {
    id: 3,
    title: "The Variation Trap",
    scenario: "They quote $10k cheaper than everyone else, but hit you with 'unexpected costs' on day 2.",
    dangerLevel: "HIGH",
    solution: "Smart Contracts",
    education: "Lowball quotes are a trap. Our AI templates ensure the Scope of Works is detailed and fixed before you start.",
    icon: <FaFileContract />
  },
  {
    id: 4,
    title: "The 'Paperless' Discount",
    scenario: "Offering a 10% discount for cash to avoid tax, insurance, and warranty trails.",
    dangerLevel: "CRITICAL",
    solution: "Document Tracking",
    education: "No paper trail means no warranty and no insurance. If the work fails, you have zero legal recourse.",
    icon: <FaMoneyBillWave />
  },
  {
    id: 5,
    title: "The Phoenix Company",
    scenario: "Builder folds the company to avoid fixing defects, then restarts under a new name the next day.",
    dangerLevel: "HIGH",
    solution: "Reputation Audit",
    education: "We track director history. If they have a history of liquidating companies, they aren't allowed on Build Start.",
    icon: <FaExclamationTriangle />
  },
  {
    id: 6,
    title: "The Material Swap",
    scenario: "Quoting for premium hardwood in the contract, but installing cheap pine when you aren't looking.",
    dangerLevel: "MEDIUM",
    solution: "Quality Monitoring",
    education: "Our platform prompts for photo evidence at key milestones to ensure materials match the specs.",
    icon: <FaSearch />
  },
  {
    id: 7,
    title: "The Termite Cover-Up",
    scenario: "Buying a house? Fresh paint might be hiding structural timber damage or rot.",
    dangerLevel: "MEDIUM",
    solution: "Verified Inspectors",
    education: "Don't trust the vendor's word. Hire a Verified Building Inspector to scan walls for thermal anomalies.",
    icon: <FaHouseDamage />
  },
  {
    id: 8,
    title: "The Off-the-Plan Mirage",
    scenario: "You pay a deposit for a new apartment. The developer goes bust, and your money is lost.",
    dangerLevel: "CRITICAL",
    solution: "Escrow Payments",
    education: "Our staged payment protection ensures funds are only released when milestones are physically verified.",
    icon: <FaHardHat />
  },
  {
    id: 9,
    title: "The Kickback Agent",
    scenario: "A Buyer's Agent pushes you to buy a specific unit because they are getting paid secretly by the developer.",
    dangerLevel: "MEDIUM",
    solution: "Transparent Fees",
    education: "Use our Verified Buyers Agents who sign a conflict-of-interest waiver. If it feels too pushy, walk away.",
    icon: <FaUserSecret />
  },
  {
    id: 10,
    title: "The Uninsured Accident",
    scenario: "A worker falls off a ladder at your home. They have no insurance. You are now liable.",
    dangerLevel: "CRITICAL",
    solution: "Expiry Tracking",
    education: "Build Start automatically tracks insurance expiry. If a Pro's policy lapses today, they lose their badge instantly.",
    icon: <FaShieldAlt />
  }
];

const ScamDetectorWidget = () => {
    const navigate = useNavigate();
  const mountRef = useRef(null);
  const [activeScamIndex, setActiveScamIndex] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState('idle'); // idle, scanning, safe

  // --- 3D SCENE LOGIC ---
  useEffect(() => {
    if (!mountRef.current) return;

    const w = mountRef.current.clientWidth;
    const h = mountRef.current.clientHeight;
    
    // Scene Setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, w / h, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(w, h);
    mountRef.current.appendChild(renderer.domElement);

    // --- OBJECT 1: THE THREAT (Spiky, Red, Wireframe) ---
    const threatGeo = new THREE.IcosahedronGeometry(2.2, 0); // Low poly = spiky
    const threatMat = new THREE.MeshPhongMaterial({ 
      color: 0xff4444, 
      wireframe: true,
      emissive: 0x550000 
    });
    const threatMesh = new THREE.Mesh(threatGeo, threatMat);
    scene.add(threatMesh);

    // --- OBJECT 2: THE SHIELD (Smooth, Green/Gold, Solid) ---
    const safeGeo = new THREE.SphereGeometry(2, 32, 32); // High poly = smooth
    const safeMat = new THREE.MeshPhongMaterial({ 
      color: 0x10b981, // Emerald Green
      emissive: 0x064e3b,
      shininess: 100,
      specular: 0xffffff,
      flatShading: false
    });
    const safeMesh = new THREE.Mesh(safeGeo, safeMat);
    safeMesh.visible = false; // Hide initially
    scene.add(safeMesh);

    // Lights
    const light = new THREE.PointLight(0xffffff, 1, 100);
    light.position.set(10, 10, 10);
    scene.add(light);
    const ambientLight = new THREE.AmbientLight(0x404040);
    scene.add(ambientLight);

    camera.position.z = 5;

    // Handle Resize
    const handleResize = () => {
        if (!mountRef.current) return;
        const newW = mountRef.current.clientWidth;
        const newH = mountRef.current.clientHeight;
        renderer.setSize(newW, newH);
        camera.aspect = newW / newH;
        camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    // Animation Loop
    let frameId;
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      
      if (scanStatus === 'idle') {
        // Red, Spiky, Rotating slowly
        threatMesh.visible = true;
        safeMesh.visible = false;
        threatMesh.rotation.x += 0.005;
        threatMesh.rotation.y += 0.005;
        threatMat.color.setHex(0xff4444); 
      } 
      else if (scanStatus === 'scanning') {
        // Yellow, Spiky, Rotating FAST
        threatMesh.visible = true;
        safeMesh.visible = false;
        threatMesh.rotation.x += 0.15;
        threatMesh.rotation.y += 0.15;
        threatMat.color.setHex(0xFCD34D); // Gold/Yellow
      } 
      else if (scanStatus === 'safe') {
        // Green, Smooth, Rotating slowly
        threatMesh.visible = false;
        safeMesh.visible = true;
        safeMesh.rotation.x += 0.01;
        safeMesh.rotation.y += 0.01;
      }

      renderer.render(scene, camera);
    };
    animate();

    // Cleanup
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', handleResize);
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      // Disposal to prevent memory leaks
      threatGeo.dispose();
      threatMat.dispose();
      safeGeo.dispose();
      safeMat.dispose();
      renderer.dispose();
    };
  }, [scanStatus]);

  // --- HANDLERS ---
  const startScan = () => {
    setIsScanning(true);
    setScanStatus('scanning');
    
    // Simulate Scan Time
    setTimeout(() => {
      setIsScanning(false);
      setScanStatus('safe');
    }, 1500);
  };

  const nextScam = () => {
    setScanStatus('idle'); // Reset 3D view
    setActiveScamIndex((prev) => (prev + 1) % scamDatabase.length);
  };

  const currentData = scamDatabase[activeScamIndex];

  return (
    <div className="scam-detector-wrapper">
      <div className="scam-header">
        <div className="scam-label">MARKET INTELLIGENCE TOOL</div>
        <h2>SCAM DETECTOR <span className="scam-accent">3000</span></h2>
        <p>The construction market is a jungle. Learn to spot the predators.</p>
      </div>

      <div className="detector-interface">
        {/* Left: The 3D Viewport */}
        <div className="viewport-container">
          <div className="three-viewport" ref={mountRef}></div>
          
          {isScanning && <div className="scan-line"></div>}
          
          <div className="viewport-overlay">
            <div className={`status-badge ${scanStatus}`}>
              {scanStatus === 'idle' && <><FaExclamationTriangle /> ACTIVE THREAT DETECTED</>}
              {scanStatus === 'scanning' && <><FaSearch /> ANALYZING...</>}
              {scanStatus === 'safe' && <><FaShieldAlt /> THREAT NEUTRALIZED</>}
            </div>
          </div>
        </div>

        {/* Right: The Data Terminal */}
        <div className="terminal-container">
          {scanStatus !== 'safe' ? (
            <div className="threat-brief fade-in">
              <div className="threat-header">
                <span className="scam-id">SCENARIO #{currentData.id}</span>
                <span className="danger-level">{currentData.dangerLevel} RISK</span>
              </div>
              
              <h3 className="threat-title">
                <span className="threat-icon">{currentData.icon}</span> 
                {currentData.title}
              </h3>
              
              <div className="terminal-screen">
                <p className="scenario-text">"{currentData.scenario}"</p>
              </div>

              <button className="btn-scan" onClick={startScan}>
                INITIATE PROTECTION SCAN
              </button>
            </div>
          ) : (
            <div className="solution-brief fade-in">
              <div className="solution-header">
                <FaCheckCircle className="check-icon" />
                <span>BUILDSTART PROTOCOL ENGAGED</span>
              </div>
              
              <h3>Solution: {currentData.solution}</h3>
              
              <div className="education-box">
                <p>{currentData.education}</p>
              </div>

              <div className="terminal-actions">
                <button className="btn-next" onClick={nextScam}>Next Threat &rarr;</button>
                
                <button className="btn-register-now" onClick={() => navigate('/role-select')}>
                   Get Protected Now
                </button>


              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScamDetectorWidget;